import React, { createContext, useState, ReactNode, useContext, useEffect, useCallback } from 'react';
import {settingsApi} from '../api/adminDataApi';

interface YearContextType {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  availableYears: number[];
  isLoading: boolean;
}

export const YearContext = createContext<YearContextType | undefined>(undefined);

export const YearProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    const saved = localStorage.getItem('samadhi_selected_year');
    return saved ? parseInt(saved, 10) : currentYear;
  });
  const [isLoading, setIsLoading] = useState(true);

  const startYear = 2024;
  const endYear = currentYear + 1;
  const availableYears = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i).reverse();

  useEffect(() => {
    const loadYear = async () => {
      try {
        const response = await settingsApi.publicGet();
        const data = response.settings;
        if (Object.keys(data).length > 0) {
          if (data.katinaYear && typeof data.katinaYear === 'number') {
            setSelectedYearState(data.katinaYear);
            localStorage.setItem('samadhi_selected_year', data.katinaYear.toString());
          }
        }
      } catch (err) {
        console.warn('Failed to load katinaYear from the Settings API, using local fallback:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadYear();
  }, []);

  const setSelectedYear = useCallback((year: number) => {
    setSelectedYearState(year);
    localStorage.setItem('samadhi_selected_year', year.toString());
  }, []);

  return (
    <YearContext.Provider value={{ selectedYear, setSelectedYear, availableYears, isLoading }}>
      {children}
    </YearContext.Provider>
  );
};

export const useYearContext = () => {
  const context = useContext(YearContext);
  if (context === undefined) {
    throw new Error('useYearContext must be used within a YearProvider');
  }
  return context;
};
